// @flow
import * as csx from 'csstips';
import includes from 'lodash/includes';
import { shapeAbbreviations } from '@src/utils/displayMaps';
import { stylesheet } from 'typestyle';
import classnames from 'classnames';

import { regularYellowText, borderGrayOutlineLight } from '@styles/general';
import colors from '@src/constants/colors';

import QuestionMarkPopup from '@components/popups/QuestionMark';
import * as ShapeContent from '@components/popups/content/AllShapeIcons';

import type { ToggleShapeType } from '@components/containers/Diamonds';

const notAvailableWidth = 170;
const defaultNotAvailableLeft = 17;
const notAvailableRightMargin = 10;

const css = stylesheet({
  container: {
    margin: '0 auto 30px auto',
    width: '280px',
    height: '95px',
    position: 'relative',
  },
  title: {
    color: colors.lumeraMidBlue,
    marginBottom: '10px',
    fontSize: '14px',
    lineHeight: '1.153846154',
  },
  iconContainer: {
    ...csx.horizontal,
    ...csx.wrap,
    margin: '0 -20px',
    $nest: {
      '& > :nth-child(1n+6)': {
        marginTop: '12px',
      },
    },
  },
  iconWrapper: {
    ...csx.layerParent,

    '-ms-flex-preferred-size': '20%',
    flexBasis: '20%',
    textAlign: 'center',
  },
  disabledIconWrapper: {
    img: {
      opacity: 0.2,
    },
  },
  icon: {
    height: '29px',
    width: '29px',
  },
  labelQuestionMark: {
    display: 'inline-block',
    marginLeft: '6px',
    position: 'relative',
    lineHeight: 0,
    verticalAlign: 'baseline',
    cursor: 'pointer',
    height: 13,
  },
  notAvailable: {
    position: 'absolute',
    top: 0,
    left: defaultNotAvailableLeft,

    display: 'block',
    borderRadius: 5,
    width: notAvailableWidth,
    textAlign: 'left',

    padding: '3px 8px',

    border: `1px solid ${borderGrayOutlineLight}`,
    backgroundColor: 'white',
    zIndex: 10,

    i: {
      color: regularYellowText,
      marginRight: 10,
    },
  },
});

const shapes = [
  {
    name: 'Round',
    abbreviation: shapeAbbreviations.Round,
    image: '/images/diamond_search_results/b.svg',
    hover: '/images/diamond_search_results/b-hover.svg',
  },
  {
    name: 'Princess-Cut',
    abbreviation: shapeAbbreviations.Princess,
    image: '/images/diamond_search_results/pr.svg',
    hover: '/images/diamond_search_results/pr-hover.svg',
  },
  {
    name: 'Oval',
    abbreviation: shapeAbbreviations.Oval,
    image: '/images/diamond_search_results/o.svg',
    hover: '/images/diamond_search_results/o-hover.svg',
  },
  {
    name: 'Marquise',
    abbreviation: shapeAbbreviations.Marquise,
    image: '/images/diamond_search_results/m.svg',
    hover: '/images/diamond_search_results/m-hover.svg',
  },
  {
    name: 'Pear-Shaped',
    abbreviation: shapeAbbreviations.Pear,
    image: '/images/diamond_search_results/p.svg',
    hover: '/images/diamond_search_results/p-hover.svg',
  },
  {
    name: 'Cushion-Cut',
    abbreviation: shapeAbbreviations.Cushion,
    image: '/images/diamond_search_results/c.svg',
    hover: '/images/diamond_search_results/c-hover.svg',
  },
  {
    name: 'Emerald-Cut',
    abbreviation: shapeAbbreviations.Emerald,
    image: '/images/diamond_search_results/e.svg',
    hover: '/images/diamond_search_results/e-hover.svg',
  },
  {
    name: 'Asscher',
    abbreviation: shapeAbbreviations.Asscher,
    image: '/images/diamond_search_results/as.svg',
    hover: '/images/diamond_search_results/as-hover.svg',
  },
  {
    name: 'Radiant-Cut',
    abbreviation: shapeAbbreviations.Radiant,
    image: '/images/diamond_search_results/r.svg',
    hover: '/images/diamond_search_results/r-hover.svg',
  },
  {
    name: 'Heart-Shaped',
    abbreviation: shapeAbbreviations.Heart,
    image: '/images/diamond_search_results/h.svg',
    hover: '/images/diamond_search_results/h-hover.svg',
  },
];

type PropsType = {
  selectableShapes: Array<string>,
  selectedShapes: Array<string>,
  onShapeChange: ToggleShapeType,
  hiddenShapes?: Array<string>,
  showHeader: boolean,
  incompatibleWord: string,
  showCompatibility: boolean,
};

type TShape = {
  shapeName: string,
  shapeAbbreviation: string,
  selectable: boolean,
  onShapeChange: (name: string) => void,
  imageUrl: string,
  incompatibleWord: string,
  showCompatibility: boolean,
};

type TNotAvailable = {
  shapeRef: { current: null | HTMLDivElement },
  incompatibleWord: string,
};

function NotAvailable({ shapeRef, incompatibleWord }: TNotAvailable) {
  const [left, setLeft] = React.useState(defaultNotAvailableLeft);

  React.useLayoutEffect(() => {
    if (!shapeRef.current) return;
    if (!document.body) return;

    const pageWidth = document.body.clientWidth;
    const { left: parentLeft } = shapeRef.current.getBoundingClientRect();

    const remainingRight = pageWidth - parentLeft;
    const spaceNeeded = notAvailableWidth + notAvailableRightMargin;
    const calculatedLeft = Math.min(
      defaultNotAvailableLeft,
      remainingRight - spaceNeeded,
    );

    setLeft(calculatedLeft);
  }, [shapeRef]);

  return (
    <div className={css.notAvailable} style={{ left }}>
      <i className='fa fa-ban' />
      Not compatible with chosen {incompatibleWord}
    </div>
  );
}

function Shape({
  shapeName,
  shapeAbbreviation,
  selectable,
  onShapeChange,
  imageUrl,
  incompatibleWord,
  showCompatibility,
}: TShape) {
  const [showAvailability, setShowAvailability] = React.useState(false);
  const shapeRef = React.useRef(null);

  const enableAvailabilityDisplay = React.useCallback(() => {
    setShowAvailability(true);
  }, []);

  const disableAvailabilityDisplay = React.useCallback(() => {
    setShowAvailability(false);
  }, []);

  return (
    <div
      className={classnames(css.iconWrapper, {
        [css.disabledIconWrapper]: !selectable,
      })}
      onMouseEnter={enableAvailabilityDisplay}
      onMouseLeave={disableAvailabilityDisplay}
      ref={shapeRef}
    >
      <img
        className={css.icon}
        onClick={() => {
          if (selectable) onShapeChange(shapeName);
        }}
        src={imageUrl}
        data-test-name={`shape-icon-${shapeAbbreviation}`}
      />

      {!selectable && showAvailability && showCompatibility && (
        <NotAvailable shapeRef={shapeRef} incompatibleWord={incompatibleWord} />
      )}
    </div>
  );
}

export default function ShapePicker(props: PropsType) {
  const {
    hiddenShapes,
    incompatibleWord,
    onShapeChange,
    selectableShapes,
    selectedShapes,
    showCompatibility,
    showHeader,
  } = props;
  const selectedShape = selectedShapes[0];

  const humanFriendlyShape = selectedShape?.replace(/-.*/, '');

  return (
    <div className={css.container}>
      {showHeader && (
        <h3 className={css.title}>
          {humanFriendlyShape || 'Shape'}
          <QuestionMarkPopup initialContent={ShapeContent} />
        </h3>
      )}
      <div className={css.iconContainer}>
        {shapes.map((s, i) => {
          if (includes(hiddenShapes, s.abbreviation)) return null;
          let selected = includes(selectedShapes, s.name);
          selected = selected || includes(selectedShapes, s.abbreviation);

          const selectable = includes(selectableShapes, s.abbreviation);
          const imageUrl = selectable && selected ? s.hover : s.image;

          return (
            <Shape
              shapeName={s.name}
              shapeAbbreviation={s.abbreviation}
              key={s.name}
              imageUrl={imageUrl}
              selectable={selectable}
              incompatibleWord={incompatibleWord}
              showCompatibility={showCompatibility}
              onShapeChange={onShapeChange}
            />
          );
        })}
      </div>
    </div>
  );
}

ShapePicker.defaultProps = {
  selectableShapes: ['B', 'PR', 'O', 'M', 'P', 'C', 'E', 'AS', 'R', 'H'],
  showHeader: true,
  incompatibleWord: 'setting',
  showCompatibility: true,
};
